import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { all_routes } from "../../router/all_routes";
import ReactApexChart from "react-apexcharts";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import {
  fetchAllLeaveReport,
  fetchAllLeaveReportDetails,
} from "../../../core/data/redux/reports/leave_reports/leavereports.Action";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchAllLeaveRequests } from "../../../core/data/redux/attendance/leave/leaveAction";
import { formatDate } from "../../../core/utils/FormatData";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import {
  exportToCSV,
  exportToExcel,
} from "../../../core/common/GlobalFunctions/exportTableData";
import { fetchAllLeaveledgerReport } from "../../../core/data/redux/reports/leaveledger/leaveledger.Action";

interface LeaveLedgerReportProps {
  employeeFilter?: string | null;
  departmentFilter?: string | null;
  designationFilter?: string | null;
  year?: string;
  month?: string;
  showOnlyTable?: boolean;
}

interface LeaveLedgerEntry {
  EmployeeID: number;
  EmployeeName: string;
  DepartmentName: string;
  DepartmentId: number;
  DesignationName: string;
  DesignationId: number;
  LeaveTypeName: string;
  Frequency: string;
  TotalLeaves: number;
  LeavesTaken: number;
  Balance: number;
  Action: string;
  ApplicableFrom: string;
  CreatedAt: string;
}


interface Props {
  data: LeaveLedgerEntry[];
}


const Leaveledgerreport: React.FC<LeaveLedgerReportProps> = ({
  employeeFilter,
  departmentFilter,
  designationFilter,
  year,
  month,
  showOnlyTable
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [leaveConfig, setleaveConfig] = useState<any>(null);
  const [selectedSort, setSelectedSort] = useState<any>("Recently Added");
  const { leaveRequests } = useSelector((state: any) => state.attendance.leaveRequests);
  const [leavecountDetails, setleavecountDetails] = useState<any>(null);
  const [selectedYear, setSelectedYear] = useState("2025");
  const [tableKey, setTableKey] = useState(0);
  const data = leaveRequests ? leaveRequests : [];
  const [leaveledger, setleaveledger] = useState<any>([])


  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [dispatch, leaveRequests]);

  useEffect(() => {
    dispatch(fetchAllLeaveRequests());
    dispatch(fetchAllLeaveReport(selectedYear)).then((res) => {
      setleavecountDetails(res.payload.data);
    });
    dispatch(fetchAllLeaveReportDetails(selectedYear)).then((res) => {
      setleaveConfig(res.payload.data);
    });
    dispatch(fetchAllLeaveledgerReport())
      .then((res) => {
        setleaveledger(res.payload.data)
      })
  }, []);

  useEffect(() => {
    if (selectedYear) {
      dispatch(fetchAllLeaveReportDetails(selectedYear))
        .then((res) => {
          setleaveConfig(res.payload.data);
        })
        .catch((err: any) => {
          console.error("Failed to fetch Leave data:", err);
        });
    }
  }, [selectedYear]);

  const handleYearSelect = (year: any) => {
    setSelectedYear(year);
  };

  const filteredData = useMemo(() => {
    return filterData(
      data || [],
      statusFilter ? { LeaveRequestStatus: statusFilter } : {},
      "CreatedAt",
      "CreatedAt",
      selectedSort
    );
  }, [dispatch, data, leaveRequests, selectedSort, statusFilter]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [dispatch, data, leaveRequests, selectedSort, statusFilter]);


  const getActionColor = (action: any) => {
    switch (action) {
      case "created":
        return "success";
      case "updated":
        return "info";
      case "used":
        return "danger";
      case "request":
        return "warning";
      default:
        return "secondary";
    }
  };

  const LeaveLedgerReport: React.FC<Props> = ({ data }) => {
    const [employeeList, setEmployeeList] = useState<any[]>([]);
    const [departmentList, setDepartmentList] = useState<any[]>([]);
    const [designationList, setDesignationList] = useState<any[]>([]);
    const [filters, setFilters] = useState({
      employeeId: "",
      departmentId: "",
      designationId: "",
    });

    useEffect(() => {
      if (employeeFilter) {
        setFilters(prev => ({ ...prev, employeeId: employeeFilter }));
      }
      if (departmentFilter) {
        setFilters(prev => ({ ...prev, departmentId: departmentFilter }));
      }
      if (designationFilter) {
        setFilters(prev => ({ ...prev, designationId: designationFilter }));
      }
    }, [employeeFilter, departmentFilter, designationFilter]);



    useEffect(() => {
      if (data && data.length > 0) {
        const employees = data.map((d: any) => ({
          EmployeeId: d.EmployeeID,
          Name: d.EmployeeName,
          DepartmentName: d.DepartmentName,
          DesignationName: d.DesignationName,
        }));

        const departments = Array.from(
          new Map(
            data.map((d: any) => [
              d.DepartmentName,
              { Id: d.DepartmentName, Name: d.DepartmentName },
            ])
          ).values()
        );

        const designations = Array.from(
          new Map(
            data.map((d: any) => [
              d.DesignationName,
              { Id: d.DesignationName, Name: d.DesignationName },
            ])
          ).values()
        );

        setEmployeeList(employees);
        setDepartmentList(departments);
        setDesignationList(designations);
      }
    }, [data]);

    // ✅ Apply filters directly on API data
    const filteredData = useMemo(() => {
      return (data || []).filter((emp: any) => {

        const matchEmployee =
          !filters.employeeId ||
          emp.EmployeeID === Number(filters.employeeId) ||
          emp.EmployeeName?.toLowerCase().includes(filters.employeeId?.toLowerCase());

        const matchDepartment =
          !filters.departmentId ||
          emp.DepartmentName?.toLowerCase().includes(filters.departmentId?.toLowerCase());

        const matchDesignation =
          !filters.designationId ||
          emp.DesignationName?.toLowerCase().includes(filters.designationId?.toLowerCase());

        return matchEmployee && matchDepartment && matchDesignation;
      });
    }, [data, filters]);

    return (
      <div className="container mt-4">
        <h3 className="mb-4">Leave Ledger Report</h3>

        {/* Filters */}
        <div className="row mb-4">
          {/* Employee Filter */}
          <div className="col-md-4">
            <label>Filter by Employee</label>
            <select
              className="form-control"
              value={filters.employeeId}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, employeeId: e.target.value }))
              }
            >
              <option value="">All Employees</option>
              {employeeList.map((emp) => (
                <option key={emp.EmployeeId} value={emp.EmployeeId}>
                  {emp.Name}
                </option>
              ))}
            </select>
          </div>

          {/* Department Filter */}
          <div className="col-md-4">
            <label>Filter by Department</label>
            <select
              className="form-control"
              value={filters.departmentId}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, departmentId: e.target.value }))
              }
            >
              <option value="">All Departments</option>
              {departmentList.map((dep) => (
                <option key={dep.Id} value={dep.Id}>
                  {dep.Name}
                </option>
              ))}
            </select>
          </div>

          {/* Designation Filter */}
          <div className="col-md-4">
            <label>Filter by Designation</label>
            <select
              className="form-control"
              value={filters.designationId}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  designationId: e.target.value,
                }))
              }
            >
              <option value="">All Designations</option>
              {designationList.map((des) => (
                <option key={des.Id} value={des.Id}>
                  {des.Name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filtered Report */}
        {filteredData.map((employee: any) => (
          <div
            key={employee.EmployeeID}
            className="mb-5 border rounded p-3 shadow-sm"
          >
            <h5 className="text-primary">{employee.EmployeeName}</h5>
            <p>
              <b>Department:</b> {employee.DepartmentName} | <b>Designation:</b>{" "}
              {employee.DesignationName}
            </p>
            <div className="table-responsive mt-3">
              <table className="table table-bordered table-sm">
                <thead className="table-light">
                  <tr>
                    <th>Leave Date</th>
                    <th>Leave Type</th>
                    <th>Action</th>
                    <th>Credit</th>
                    <th>Debit</th>
                    <th>Remaining Balance</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {employee.Ledgers && employee.Ledgers.length > 0 ? (
                    employee.Ledgers.map((leave: any, idx: number) => (
                      <tr key={idx}>
                        <td>{leave.LeaveDate}</td>
                        <td>{leave.LeaveTypeName}</td>
                        <td>{leave.Action}</td>
                        <td>{leave.CreditQty}</td>
                        <td>{leave.DebitQty}</td>
                        <td>{leave.LeaveQuantity}</td>
                        <td>{leave.Remarks}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center text-muted">
                        No ledger entries
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  };




  useEffect(() => {
    const approvedPerMonth = new Array(12).fill(0);
    const pendingPerMonth = new Array(12).fill(0);
    const rejectedPerMonth = new Array(12).fill(0);

    if (leaveConfig && leaveConfig.length > 0) {
      leaveConfig.forEach(({ month, approved, pending, rejected }: any) => {
        const index = month - 1; // month is 1-based
        approvedPerMonth[index] = parseInt(approved);
        pendingPerMonth[index] = parseInt(pending);
        rejectedPerMonth[index] = parseInt(rejected);
      });
    }

    setLeavechart((prev: any) => ({
      ...prev,
      series: [
        {
          ...prev.series[0],
          data: approvedPerMonth,
        },
        {
          ...prev.series[1],
          data: pendingPerMonth,
        },
        {
          ...prev.series[2],
          data: rejectedPerMonth,
        },
      ],
    }));
  }, [leaveConfig, dispatch, leaveRequests]);

  //New Chart
  const [leavechart, setLeavechart] = useState<any>({
    series: [
      {
        name: "Approved",
        data: new Array(12).fill(0),
      },
      {
        name: "Pending",
        data: new Array(12).fill(0),
      },
      {
        name: "Rejected",
        data: new Array(12).fill(0),
      },
    ],
    chart: {
      type: "bar",
      height: 210, // Change this value to your desired height
      stacked: true,
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
            offsetX: -10,
            offsetY: 0,
          },
        },
      },
    ],
    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ], // Update to match your time frame
    },
    yaxis: {
      labels: {
        offsetX: -15,
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      show: false,
    },
    colors: ["#03C95A", "#FFC107", "#0C4B5E", "#F26522"],
    dataLabels: {
      enabled: false,
    },
  });

    if (showOnlyTable) {
  return (
    <div className="card">

            <div className="card-body p-0">
              <LeaveLedgerReport data={leaveledger} />
            </div>
          </div>
  );
}

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Leave Ledger Report</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="index.html">
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Report</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Leave Ledger Report
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" />
                    Export
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <button
                        onClick={() => exportToCSV(data, "Leave_report")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-pdf me-1" />
                        Export as CSV
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => exportToExcel(data, "Leave_report")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as Excel{" "}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="row">
            <div className="col-xl-6 d-flex">
              <div className="row flex-fill">
                {/* Total Companies */}
                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2 overflow-hidden">
                        <div>
                          <p className="fs-12 fw-normal mb-1 text-truncate">
                            Total Leaves
                          </p>
                          <h4>{leavecountDetails?.[0].TotalLeaves}</h4>
                        </div>
                        <div className="leave-report-icon">
                          <Link to="#">
                            <span className="p-2 border border-primary bg-transparent-primary rounded-circle d-flex align-items-center justify-content-center">
                              <i className="ti ti-calendar-x text-primary" />
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Total Companies */}
                {/* Total Companies */}
                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2 overflow-hidden">
                        <div>
                          <p className="fs-12 fw-normal mb-1 text-truncate">
                            Approved Leaves
                          </p>
                          <h4>{leavecountDetails?.[0].ApprovedLeaves || 0}</h4>
                        </div>
                        <div className="leave-report-icon">
                          <Link to="#">
                            <span className="p-2 border border-success bg-transparent-success rounded-circle d-flex align-items-center justify-content-center">
                              <i className="ti ti-calendar-x text-success" />
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Total Companies */}
                {/* Inactive Companies */}
                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2 overflow-hidden">
                        <div>
                          <p className="fs-12 fw-normal mb-1 text-truncate">
                            Pending Requests
                          </p>
                          <h4>{leavecountDetails?.[0].PendingLeaves || 0}</h4>
                        </div>
                        <div className="leave-report-icon">
                          <Link to="#">
                            <span className="p-2 border border-skyblue bg-transparent-skyblue rounded-circle d-flex align-items-center justify-content-center">
                              <i className="ti ti-calendar-x text-skyblue" />
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Inactive Companies */}
                {/* Company Location */}
                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2 overflow-hidden">
                        <div>
                          <p className="fs-12 fw-normal mb-1 text-truncate">
                            Rejected Leaves
                          </p>
                          <h4>{leavecountDetails?.[0].RejectedLeaves || 0}</h4>
                        </div>
                        <div className="leave-report-icon">
                          <Link to="#">
                            <span className="p-2 border border-danger bg-transparent-danger rounded-circle d-flex align-items-center justify-content-center">
                              <i className="ti ti-calendar-x text-danger" />
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Company Location */}
              </div>
            </div>
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header border-0 pb-0">
                  <div className="d-flex flex-wrap justify-content-between align-items-center row-gap-2">
                    <div className="d-flex align-items-center ">
                      <span className="me-2">
                        <i className="ti ti-chart-bar text-danger" />
                      </span>
                      <h5>Leaves </h5>
                    </div>
                    <div className="d-flex align-items-center">
                      <p className="d-inline-flex align-items-center me-2 mb-0">
                        <i className="ti ti-square-filled fs-12 text-success me-2" />
                        Approved
                      </p>
                      <p className="d-inline-flex align-items-center mb-0 me-2">
                        <i className="ti ti-square-filled fs-12 text-warning me-2" />
                        Pending
                      </p>
                      <p className="d-inline-flex align-items-center mb-0 me-2">
                        <i
                          className="ti ti-square-filled fs-12 me-2"
                          style={{ color: "#0C4B5E" }}
                        />
                        Rejected
                      </p>
                    </div>

                    <div className="dropdown">
                      <Link
                        to="#"
                        className="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                        data-bs-toggle="dropdown"
                      >
                        {selectedYear}
                      </Link>
                      <ul className="dropdown-menu  dropdown-menu-end p-2">
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2025")}
                          >
                            2025
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2024")}
                          >
                            2024
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2023")}
                          >
                            2023
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="card-body py-0">
                  <ReactApexChart
                    id="payslip-chart"
                    options={leavechart}
                    series={leavechart.series}
                    type="bar"
                    height={210}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card">

            <div className="card-body p-0">
              <LeaveLedgerReport data={leaveledger} />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default Leaveledgerreport;
